import { Meta } from './Meta/Meta';
import { HeaderFeature } from '../Header/HeaderFeature';
import { UI_Layout } from 'ui/components/4-habitats/Layout/Layout';
import { useRouter } from 'next/router';
import { PageProps } from 'domain/page/pageProps';
import { PropsWithChildren, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { isLightColor } from 'helpers/types';
import Image from 'next/image';

export const LayoutFeature: React.FC<PageProps & PropsWithChildren> = ({ children, content }) => {
	const { page, siteTheme = [] } = content;
	const { primaryColor, secondaryColor, lightHeader } = siteTheme[0]?.content?.properties?.theme?.properties ?? {};
	const pageHeader = (page?.properties as Partial<Content.PageHeaders> | undefined)?.pageHeader;
	const firstHeroCard = pageHeader?.find((block) => block.documentType === 'heroCard');
	const firstHeroCardProperties = firstHeroCard?.content?.properties;
	const firstHeroCardHasOverlayImage =
		!!(firstHeroCardProperties?.image?.url || firstHeroCardProperties?.mobileImage?.url) && !firstHeroCard?.settings?.properties?.noOverlay;
	const firstHeroCardIsLight = firstHeroCard != null && isLightColor(firstHeroCardProperties?.color);
	const isArticlePage = page?.documentType === 'articlePage';
	const isFilteredLibraryPage = page?.documentType === 'filteredLibraryPage';
	const effectiveLightHeader = firstHeroCardHasOverlayImage
		? true
		: firstHeroCardIsLight || isArticlePage || isFilteredLibraryPage
		? false
		: lightHeader;
	const router = useRouter();
	const [isTransitioning, setIsTransitioning] = useState(false);
	useEffect(() => {
		setIsTransitioning(true);

		const timeoutId = setTimeout(() => setIsTransitioning(false), 300);

		return () => clearTimeout(timeoutId);
	}, [router.asPath]);

	const themeStyle =
		primaryColor &&
		secondaryColor &&
		({
			'--color-primary': primaryColor,
			'--color-secondary': secondaryColor,
		} as React.CSSProperties);

	type NextImageProps = React.ComponentProps<typeof Image>;
	const ImageOptimization = (props: NextImageProps) => {
		return <Image {...props} alt={props?.alt ?? ''} />;
	};
	return (
		<div style={themeStyle}>
			<UI_Layout router={router} imageOptimization={ImageOptimization}>
				<Meta {...page?.properties} />
				<HeaderFeature lightHeader={effectiveLightHeader} isTransitioning={!isTransitioning} />
				<AnimatePresence initial={false}>
					<motion.div
						key={router.asPath}
						initial="initialState"
						animate="animateState"
						exit="exitState"
						transition={{
							type: 'tween',
							duration: 0.3,
						}}
						variants={{
							initialState: {
								opacity: 0,
								y: '25vh',
							},
							animateState: {
								opacity: 1,
								y: 0,
							},
							exitState: {
								opacity: 0,
								y: '-25vh',
							},
						}}
						className="page-transition"
					>
						{children}
					</motion.div>
				</AnimatePresence>
			</UI_Layout>
		</div>
	);
};
